import { IMetricMethodologySettingsUI, OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { handleRequestsInBatches } from "@gtmhub/util";
import { IQService, ITimeoutService, IPromise, IAngularEvent, IScope, IFilterService } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalService, IModalScope } from "angular-ui-bootstrap";
import dayjs from "@gtmhub/dayjs";
import { IGoal } from "@gtmhub/goals/models";
import { IAssigneeIdMap } from "@gtmhub/assignees/models";
import { ICollection, INumericMap, IGtmhubRootScopeService } from "@gtmhub/models";
import { IAggregatedReactionsMap, IAggregatedReaction, IReaction } from "@gtmhub/experiments/models/models";
import { customNotFoundError, IIndicatorErrorOptions, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { GoalService } from "@gtmhub/goals/goalService";
import { CommentService, IComment } from "@gtmhub/comments";
import { ReactionService } from "@gtmhub/experiments/services/reaction.service";
import { IStateInit } from "@gtmhub/core/routing";
import { DisableMetricSyncModal, MetricService, MetricValueFilter } from "@gtmhub/okrs/metrics";
import { ICheckin, IMetric, IMetricSnapshot } from "../models/models";
import { ITask } from "@gtmhub/tasks/models/models";
import { TaskService } from "@gtmhub/tasks/services/taskService";
import { GoalModalsService } from "@gtmhub/goals/goalModalsService";
import { emptyDateUTC } from "@gtmhub/util";
import { ITaskCreatedArgs, ITaskDeletedArgs, ITaskUpdatedArgs, TaskEvents } from "@gtmhub/tasks";
import { INgRedux } from "@gtmhub/state-management";
import { CloneOkrsModal, GoalsChangedEventArgs, OkrEvents } from "@gtmhub/okrs";
import { ProgressColoring } from "@gtmhub/configuration";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IPlanningSession, PlanningSessionStatus, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { PermissionsService, getCurrentUserId, hasCurrentUserRole } from "@gtmhub/users";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { prepareCheckinData } from "@gtmhub/web-workers/shared";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { SizeOfGif } from "@gtmhub/shared/services/tenor-gif.service";
import { CustomFieldActions, ICustomField, ICustomFieldsStoreState } from "@gtmhub/customFields";
import { getParentState, isOkrStatusAbandoned } from "@gtmhub/goals/utils";
import { copyToClipboard } from "@gtmhub/shared/dom";
import { getCopyURLNotificationEl } from "@gtmhub/shared/utils/dom-utils";
import { localize } from "@gtmhub/localization";

interface IMetricReduxScope {
  assigneesIdMap: IAssigneeIdMap;
  sessionIdMap: Record<string, IPlanningSession>;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
  customFieldsCheckin: ICollection<ICustomField>;
}

const bindStateToScope = (state: ICustomFieldsStoreState & IAssigneesStoreState & ISessionsStoreState): IMetricReduxScope => {
  return {
    customFieldsCheckin: { items: state.customFields.map.metric_snapshot },
    assigneesIdMap: state.assignees.map,
    sessionIdMap: state.sessions.map,
    reduxItemsFetched: state.assignees.isFetched && state.sessions.isFetched && state.customFields.isFetched,
    reduxItemsError: state.assignees.error || state.sessions.error,
  };
};

export type MetricField = "name" | "description" | "initialValue" | "target" | "ownerId" | "ownerIds" | "settings";
export type SnapshotOrigin = "cascading_update" | "automatic_update" | "manual_update" | "notes_update" | "unsupported";

export interface IUpdateMetricField {
  updateField(metric: IMetric, errorField: MetricField): IPromise<IMetric>;
}

export interface IMetricScope extends IUpdateMetricField, IScope, IModalScope, IExtendedRootScopeService, IMetricReduxScope {
  // to do: add ctrl specific interface/type fetched with https://gtmhub.atlassian.net/browse/GVS-10052
  metric: IMetric;

  indicators: {
    deleting: boolean;
    gettingTasks: boolean;
    loadingMetric: boolean;
    loadingUpdate: boolean;
    refreshingMetric: boolean;
    dontDisplaySnapshots: boolean;
  };
  goal: IGoal;
  editMode: {
    name: boolean;
    description: boolean;
    initialValue: boolean;
    target: boolean;
  };
  errorMessage: {
    [key in MetricField]: {
      state: boolean;
      error: string;
    };
  };
  comments: any;
  parentType: string;
  planningSession: IPlanningSession;
  currentUserId: string;
  isAdmin: boolean;
  msteams: boolean;
  reactions: IAggregatedReactionsMap;
  contributingKRsTarget: {
    isCovered: boolean;
    totalAmount: IMetric; // IMetric is used so we can format the targetValue
    difference: IMetric;
  };
  taskList: ITask[];
  sourceMetric: IMetric;
  sizeOfGif: typeof SizeOfGif;
  contributingMetrics: IMetric[];
  parentState: string; // this is used to provide metadata for tracking
  methodologySettings: IMetricMethodologySettingsUI;

  getSnapshotSortField(snapshot: IMetricSnapshot): number;
  getSnapshotOrigin(snapshot: IMetricSnapshot, metric: IMetric, sourceMetric: IMetric): SnapshotOrigin;
  getFormattedValue(snapshot): string;
  allowEdit: boolean;
  allowDelete: boolean;
  redirectToGoal(sessionId: string, goalId: string);
  closeMetricForm(): void;
  confirmDeleteSnapshot(snapshotId: string): void;
  cloneMetricHere(metric: IMetric): void;
  openCloneMetricModal(metric: IMetric): void;
  confirmDeleteMetric(metricId: string, goalId: string);
  openMoveMetricModal(metric: IMetric): void;
  toggleEditMode(field: MetricField, state?: boolean): void;
  updateName(): void;
  updateDescription(): void;
  updateInitialValue(): void;
  updateTarget(): void;
  updateMultipleOwners(ids: string[]): void;
  openDisableSyncModal(metricId: string): void;
  canUpdateMetric(): boolean;
  isMetricOfAbandonedGoal(): boolean;
  openMetricToUpdate(): void;
  copyItemUrlToClipboard(): void;
  isFieldPartOfWorkflow(filedName: string): boolean;
}

export interface IMetricParams {
  metricId: string;
  metricReturnState: string;
  sharedMetric?: string;
}

export class MetricCtrl implements IStateInit {
  private taskEvents: TaskEvents;
  private okrEvents: OkrEvents;
  private metricPromise: IPromise<IMetric>;
  private persistedMetric: IMetric;
  private metricActions: string[];
  private isCloseOkrFeatureEnabled: boolean;

  public static $inject = [
    "$scope",
    "$rootScope",
    "$q",
    "$uibModal",
    "MetricService",
    "$stateParams",
    "UIErrorHandlingService",
    "CustomFieldActions",
    "GoalService",
    "CommentService",
    "$timeout",
    "$state",
    "$filter",
    "ReactionService",
    "TaskService",
    "GoalModalsService",
    "$ngRedux",
    "ProgressColoring",
    "AssigneeActions",
    "PermissionsService",
    "CloneOkrsModal",
    "DisableMetricSyncModal",
    "PlanningSessionsActions",
    "FeatureToggleV2Service",
    "OkrMethodologyFacade",
  ];

  constructor(
    private $scope: IMetricScope,
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private $q: IQService,
    private $uibModal: IModalService,
    private metricService: MetricService,
    private $stateParams: IMetricParams,
    private uiErrorHandlingService: UIErrorHandlingService,
    private customFieldActions: CustomFieldActions,
    private goalService: GoalService,
    private commentService: CommentService,
    private $timeout: ITimeoutService,
    private $state: StateService,
    private $filter: IFilterService,
    private reactionService: ReactionService,
    private tasksService: TaskService,
    private goalModalsService: GoalModalsService,
    private $ngRedux: INgRedux,
    private progressColoring: ProgressColoring,
    private assigneeActions: AssigneeActions,
    private permissionsService: PermissionsService,
    private cloneOkrsModal: CloneOkrsModal,
    private disableMetricSync: DisableMetricSyncModal,
    private planningSessionsActions: PlanningSessionsActions,
    private featureToggleService: FeatureToggleV2Service,
    private okrMethodologyFacade: OkrMethodologyFacade
  ) {
    this.metricActions = [];
    this.$scope.allowEdit = false;
    this.$scope.allowDelete = false;
    this.taskEvents = new TaskEvents(this.$scope);
    this.okrEvents = new OkrEvents(this.$scope);

    this.$scope.indicators = {
      deleting: false,
      gettingTasks: false,
      loadingMetric: false,
      loadingUpdate: false,
      refreshingMetric: false,
      dontDisplaySnapshots: true,
    };
    this.$scope.sizeOfGif = SizeOfGif;
    this.$scope.contributingMetrics = [];

    // if use open insight from metric details and press back button
    if (this.$rootScope.noNavigation) {
      this.$rootScope.noNavigation = false;
    }
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);

    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.taskEvents.onTaskCreated((args) => this.onTaskCreated(args));
    this.taskEvents.onTaskDeleted((args) => this.onTaskDeleted(args));
    this.taskEvents.onTaskUpdated((args) => this.onTaskUpdated(args));
    this.$scope.$on("commentPosted", this.refreshMetric);
    this.$scope.$on("commentDeleted", this.refreshMetric);
    const removeReactionCreatedListener = this.$rootScope.$on("reactionCreated", this.onReactionCreated);
    const removeReactionDeletedListener = this.$rootScope.$on("reactionDeleted", this.onReactionDeleted);
    this.$scope.$on("$destroy", () => {
      unsubscribe();
      removeReactionCreatedListener();
      removeReactionDeletedListener();
    });

    this.$scope.getSnapshotSortField = this.getSnapshotSortField;
    this.$scope.getSnapshotOrigin = this.getSnapshotOrigin;
    this.$scope.getFormattedValue = this.getFormattedValue;
    this.$scope.redirectToGoal = this.redirectToGoal;
    this.$scope.closeMetricForm = this.closeMetricForm;
    this.$scope.confirmDeleteSnapshot = this.confirmDeleteSnapshot;
    this.$scope.confirmDeleteMetric = this.confirmDeleteMetric;
    this.$scope.cloneMetricHere = this.cloneMetricHere;
    this.$scope.openCloneMetricModal = this.openCloneMetricModal;
    this.$scope.openMoveMetricModal = this.openMoveMetricModal;
    this.$scope.toggleEditMode = this.toggleEditMode;
    this.$scope.updateField = this.updateField;
    this.$scope.updateDescription = this.updateDescription;
    this.$scope.updateName = this.updateName;
    this.$scope.updateInitialValue = this.updateInitialValue;
    this.$scope.updateTarget = this.updateTarget;
    this.$scope.updateMultipleOwners = this.updateMultipleOwners;
    this.$scope.openDisableSyncModal = this.openDisableSyncModal;
    this.$scope.canUpdateMetric = this.canUpdateMetric;
    this.$scope.isMetricOfAbandonedGoal = this.isMetricOfAbandonedGoal;
    this.$scope.openMetricToUpdate = this.openMetricToUpdate;
    this.$scope.copyItemUrlToClipboard = this.copyItemUrlToClipboard;
    this.$scope.isFieldPartOfWorkflow = (fieldName) => this.isFieldPartOfWorkflow(fieldName);

    this.$scope.errorMessage = {
      description: {
        state: false,
        error: null,
      },
      name: {
        state: false,
        error: null,
      },
      initialValue: {
        state: false,
        error: null,
      },
      target: {
        state: false,
        error: null,
      },
      ownerId: {
        state: false,
        error: null,
      },
      ownerIds: {
        state: false,
        error: null,
      },
      settings: {
        state: false,
        error: null,
      },
    };
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private isFieldPartOfWorkflow(fieldName: string): boolean {
    return this.metricService.isFieldPartOfWorkflow(fieldName, this.$scope.planningSession);
  }

  canUpdateMetric = (): boolean => {
    if (this.isCloseOkrFeatureEnabled) {
      return this.$rootScope.hasPermission("ManageGoals") && !this.$scope.metric.sourceMetricId && !this.isMetricOfAbandonedGoal();
    } else {
      return this.$rootScope.hasPermission("ManageGoals") && !this.$scope.metric.sourceMetricId;
    }
  };

  isMetricOfAbandonedGoal = (): boolean => {
    return isOkrStatusAbandoned(this.$scope.metric.goalClosedStatus, { isCloseOkrFeatureEnabled: this.isCloseOkrFeatureEnabled });
  };

  openMetricToUpdate = (): void => {
    if (this.canUpdateMetric()) {
      this.$state.go("._updateMetric.update", { metricId: this.$scope.metric.id });
    }
  };

  private strings = {
    msTeamsHomeState: "gtmhub.msTeamsHome",
    comments: "comments",
    reactions: "reactions",
    double: "double",
    boolean: "boolean",
    empty: "",
    at_least: "at_least",
    at_most: "at_most",
    should_stay_above: "should_stay_above",
    should_stay_below: "should_stay_below",
  };

  private closeMetricForm = (): void => {
    this.$state.go(this.strings.msTeamsHomeState);
  };

  private load = (): IPromise<unknown> => {
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());

    return this.$q
      .all([
        this.getMetricActions(this.$stateParams.metricId),
        this.$scope.watchUntilAndRejectOnError(
          () => this.$scope.reduxItemsFetched,
          () => this.$scope.reduxItemsError
        ),
      ])
      .then(() => {
        this.getMetric(this.$stateParams.metricId).then(
          (metric) => {
            this.featureToggleService.isFeatureAvailable("okrs-shareable-urls").then((enabled) => {
              if (enabled && !metric.goalId && metric.obfuscated) {
                const err = {
                  type: "private_okr",
                  title: localize("this_okr_is_private"),
                };
                const indicatorOptions: IIndicatorErrorOptions = this.$scope.indicators.loadingUpdate
                  ? { indicator: "loadingUpdate", inline: true }
                  : { indicator: "loadingMetric" };
                this.uiErrorHandlingService.handleIndicator(err, indicatorOptions);
              } else {
                this.$scope.indicators.loadingUpdate = false;
                this.$scope.indicators.loadingMetric = false;
                this.$scope.metric = metric;
                this.persistedMetric = angular.copy(metric);

                this.processMetric(metric);
                this.processContributingKRs(metric);
                this.getGoal(metric.goalId);
                this.getSourceMetric(metric.sourceMetricId);
                this.getTasks();
                this.getContributingMetricsNames(metric);
              }
            });
          },
          (err: IUIError) => {
            const indicatorOptions: IIndicatorErrorOptions = this.$scope.indicators.loadingUpdate
              ? { indicator: "loadingUpdate", inline: true }
              : { indicator: "loadingMetric" };

            this.uiErrorHandlingService.handleIndicator(err, indicatorOptions);
          }
        );
      });
  };

  private init = (): IPromise<unknown> => {
    this.addReturnStateForSharedUrl();

    this.$scope.indicators.loadingMetric = true;
    this.$scope.indicators.dontDisplaySnapshots = true;

    this.$scope.currentUserId = getCurrentUserId();
    this.$scope.isAdmin = hasCurrentUserRole("admin");
    this.$scope.parentType = "metric";
    this.$scope.taskList = [];
    this.$scope.editMode = {
      name: false,
      description: false,
      initialValue: false,
      target: false,
    };
    this.$scope.parentState = getParentState(this.$state.current.name);

    return this.featureToggleService.isFeatureAvailable("feature-close-okr").then((enabled) => {
      this.isCloseOkrFeatureEnabled = enabled;
      this.load();
    });
  };

  private addReturnStateForSharedUrl = (): void => {
    if (this.$stateParams.sharedMetric) {
      // the returnState is passed to _metric() abstract state which uses the modal util method in routing
      const params = { ...this.$stateParams, sharedMetric: null, returnState: "^.^" };
      this.$state.go(".", params, { notify: false });
    }
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    switch (args.reason) {
      case "metricMoved":
        this.onMetricMoved(args.detail);
        break;
      case "metricPatched":
        if (!args.detail.patch) {
          break;
        }
        if (
          args.detail.patch.initialValue !== undefined ||
          args.detail.patch.target !== undefined ||
          args.detail.patch.targetOperator !== undefined ||
          args.detail.patch.actual !== undefined ||
          args.detail.patch.customFields !== undefined
        ) {
          this.refresh();
        }
        break;
      case "metricUpdated":
        this.refresh();
        break;
      case "metricCommentEdit":
        this.refresh();
        break;
      case "metricSnapshotDeleted":
        this.refresh();
        break;
    }
  };

  private updateDescription = (): void => {
    this.updateField({ description: this.$scope.metric.description }, "description");
  };

  private updateField = (update: Partial<IMetric>, updatedField: MetricField): IPromise<IMetric> => {
    if (this.metricPromise) {
      const updatePromise: IPromise<{ metric: IMetric; error: IUIError }> = this.metricPromise.then((metric) =>
        this.patch(update, updatedField)
          .then((updatedMetric) => ({ metric: updatedMetric, error: null }))
          .catch((error) => ({ metric, error }))
      );
      this.metricPromise = updatePromise.then(({ metric }) => metric);

      return updatePromise.then(({ metric, error }) => {
        this.$scope.errorMessage[updatedField].state = false;
        if (error) {
          this.$scope.errorMessage[updatedField].state = true;
          return this.$q.reject(error);
        }

        return metric;
      });
    } else {
      this.metricPromise = this.patch(update, updatedField);
    }

    return this.metricPromise;
  };

  private patch = (partialMetric: Partial<IMetric>, updatedField: MetricField): IPromise<IMetric> => {
    return this.$rootScope.traceAction(`update_KR_fields-${updatedField}`, () => {
      partialMetric.id = this.$scope.metric.id;
      this.$scope.errorMessage[updatedField].state = false;

      const shouldCheckin =
        partialMetric.initialValue !== undefined && partialMetric.initialValue !== this.$scope.metric.actual && !this.$scope.metric.metricHistory?.length;

      const patch = (partialMetric: Partial<IMetric>): IPromise<IMetric> => {
        return this.metricService.patchMetric(partialMetric).then(
          (updatedMetric) => {
            this.okrEvents.broadcastGoalsChanged("goalChanged");
            this.$rootScope.$broadcast("goalsChanged");
            Object.assign(this.persistedMetric, partialMetric);
            this.$scope.errorMessage[updatedField].state = false;

            return updatedMetric;
          },
          (error) => {
            this.metricPromise = null;
            this.$scope.errorMessage[updatedField].state = true;
            return error;
          }
        );
      };

      if (shouldCheckin) {
        const checkin: ICheckin = {
          checkInDate: new Date().toISOString(),
          actual: this.$scope.metric.actual,
          confidence: this.$scope.metric.confidence?.value,
          changedBy: getCurrentUserId(),
          customFields: this.$scope.metric.customFields,
        };
        return this.metricService.checkin({ id: this.$scope.metric.id }, checkin).then(() => {
          this.okrEvents.broadcastGoalsChanged("metricSnapshotCreated", {
            checkin,
            metric: this.$scope.metric,
            goal: this.$scope.goal,
            session: this.$scope.planningSession,
          });
          return patch(partialMetric);
        });
      } else {
        return patch(partialMetric);
      }
    });
  };

  private toggleEditMode = (field: MetricField, state?: boolean): void => {
    if (!this.$scope.allowEdit) {
      return;
    }

    this.$scope.editMode[field] = state === false ? false : true;

    setTimeout(() => {
      const fieldEl = document.getElementById(`single-metric-${field}`);
      if (fieldEl) {
        fieldEl.focus();
      }
    });
  };

  private updateName = (): void => {
    if (this.$scope.metric.name === this.persistedMetric.name) {
      return;
    }
    this.updateField({ name: this.$scope.metric.name }, "name");
  };

  private updateMultipleOwners = (ids: string[]): void => {
    if (ids.length === 0) {
      return;
    }

    if (this.$scope.metric.ownerIds.toString() === ids.toString()) {
      return;
    }

    this.$scope.metric.ownerIds = ids;
    this.updateField({ ownerIds: ids }, "ownerIds");
  };

  private updateInitialValue = (): void => {
    this.$scope.metric.initialValue = typeof this.$scope.metric.initialValue === "undefined" ? this.persistedMetric.initialValue : this.$scope.metric.initialValue;
    this.validateFieldValue("initialValue");
    if (this.$scope.errorMessage.initialValue.state || this.$scope.metric.initialValue === this.persistedMetric.initialValue) {
      return;
    }
    this.updateField({ initialValue: Number(this.$scope.metric.initialValue) }, "initialValue");
  };

  private updateTarget = (): void => {
    this.$scope.metric.target = typeof this.$scope.metric.target === "undefined" ? this.persistedMetric.target : this.$scope.metric.target;
    this.validateFieldValue("target");
    if (this.$scope.errorMessage.target.state || this.$scope.metric.target === this.persistedMetric.target) {
      return;
    }
    this.updateField({ target: Number(this.$scope.metric.target) }, "target");
  };

  private validateFieldValue = (field: MetricField): void => {
    this.$scope.errorMessage[field].error = this.validateDoubleInputValues(
      this.persistedMetric.manualType,
      this.persistedMetric.targetOperator,
      field === "initialValue" ? this.$scope.metric.initialValue : this.persistedMetric.initialValue,
      field === "target" ? this.$scope.metric.target : this.persistedMetric.target,
      this.persistedMetric.critical
    );
    if (this.$scope.errorMessage[field].error) {
      this.$scope.errorMessage[field].state = true;
    } else {
      this.$scope.errorMessage[field].state = false;
    }
  };

  private validateDoubleInputValues = (manualType: string, targetOperator: string, initialValue: number, targetValue: number, criticalValue: number): string => {
    const isManualTypeDoubleOrEmpty = manualType === this.strings.double || manualType === this.strings.empty ? true : false;

    if (isManualTypeDoubleOrEmpty && targetOperator === this.strings.at_least && initialValue >= targetValue) {
      return "initial_lower_than_target";
    }

    if (isManualTypeDoubleOrEmpty && targetOperator === this.strings.at_most && initialValue <= targetValue) {
      return "initial_greater_than_target";
    }

    if (
      isManualTypeDoubleOrEmpty &&
      targetOperator === this.strings.should_stay_above &&
      criticalValue !== null &&
      typeof criticalValue !== "undefined" &&
      criticalValue >= targetValue
    ) {
      return "critical_lower_than_target";
    }

    if (
      isManualTypeDoubleOrEmpty &&
      targetOperator === this.strings.should_stay_below &&
      criticalValue !== null &&
      typeof criticalValue !== "undefined" &&
      criticalValue <= targetValue
    ) {
      return "critical_greater_than_target";
    }

    return null;
  };

  private openMoveMetricModal = (metric: IMetric): void => {
    this.goalModalsService.openModalForMove("metric", metric, this.$scope.goal);
  };

  private cloneMetricHere = (metric: IMetric) => {
    const newMetric = { ...metric, name: `${metric.name} clone` };
    delete newMetric.id;
    delete newMetric.trend;
    delete newMetric.confidence;

    this.metricService.createMetric(newMetric).then(
      (createdMetric) => {
        this.afterClone(newMetric, createdMetric);
      },
      (err) => this.uiErrorHandlingService.handleModal(err)
    );
  };

  private openCloneMetricModal = (metric: IMetric): void => {
    this.cloneOkrsModal.openForMetricClone(metric);
  };

  private afterClone = (originalMetric: IMetric, createdMetric: IMetric) => {
    this.okrEvents.broadcastGoalsChanged("goalChanged");
    this.$rootScope.$broadcast("goalsChanged");
    this.okrEvents.broadcastGoalsChanged("metricCreated", { goalId: this.$scope.goal.id, metric: createdMetric });

    const createCheckinPromise = (item) => this.metricService.checkin(createdMetric, item.checkin).catch((err) => this.uiErrorHandlingService.handleModal(err));

    handleRequestsInBatches(prepareCheckinData([originalMetric], getCurrentUserId()), createCheckinPromise, 1, this.$q);
  };

  private confirmDeleteMetric = (metricId: string, goalId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../../../goals/views/confirm-delete-metric.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope) {
          $scope.indicators = ctrl.$scope.indicators;
          $scope.confirm = () => {
            ctrl.$rootScope.traceAction("delete_KR", () => {
              $scope.indicators.deleting = true;

              ctrl.metricService.deleteMetric(goalId, metricId).then(
                () => {
                  $scope.indicators.deleting = false;

                  ctrl.okrEvents.broadcastGoalsChanged("metricDeleted", { goalId: ctrl.$scope.goal.id, metricId: ctrl.$scope.metric.id });

                  ctrl.okrEvents.broadcastGoalsChanged("goalChanged");
                  ctrl.$rootScope.$broadcast("goalsChanged");

                  ctrl.$scope.$dismiss();
                },
                (err) => ctrl.uiErrorHandlingService.handleModal(err)
              );
            });
          };
        },
      ],
    });
  };

  private measureDueDateDifference = (metric: IMetric) => {
    if (metric.dueDate && metric.dueDate !== emptyDateUTC) {
      metric.daysLeft = dayjs(metric.dueDate).diff(dayjs()) / 86400000;
    }

    if (metric.dueDate && metric.softDueDate !== emptyDateUTC) {
      metric.daysLeftToSoftDeadline = dayjs(metric.softDueDate).diff(dayjs()) / 86400000;
    }
  };

  private initAllowedActions = (): void => {
    this.$scope.allowEdit =
      this.$rootScope.hasPermission("ManageGoals") &&
      this.metricActions.includes("update") &&
      this.$scope.planningSession.status === PlanningSessionStatus.OPEN &&
      !this.$scope.goal.locked;

    this.$scope.allowDelete =
      this.$rootScope.hasPermission("ManageGoals") &&
      this.metricActions.includes("delete") &&
      this.$scope.planningSession.status === PlanningSessionStatus.OPEN &&
      !this.$scope.goal.locked &&
      !this.isMetricOfAbandonedGoal();
  };

  private onTaskCreated = (args: ITaskCreatedArgs) => {
    if (args.task.parentId === this.$stateParams.metricId) {
      this.$scope.taskList.unshift(args.task);
    }
  };

  private onTaskDeleted = (args: ITaskDeletedArgs) => {
    this.$scope.taskList = this.$scope.taskList.filter((t) => t.id !== args.task.id);
  };

  private onMetricMoved = ({ goalId, metric }: { goalId: string; metric: IMetric }) => {
    this.$scope.metric = metric;

    this.processMetric(this.$scope.metric);
    this.getGoal(goalId);
  };

  private onTaskUpdated = (args: ITaskUpdatedArgs) => {
    // if task changed parent metric => add to task list
    if (args.task.parentId === this.$scope.metric.id && !this.$scope.taskList.some((task) => task.id === args.task.id)) {
      this.$scope.taskList.unshift(args.task);
      return;
    }

    for (let i = 0; i < this.$scope.taskList.length; i++) {
      if (this.$scope.taskList[i].id === args.task.id) {
        // if parent metric of task is changed, remove from metric's task list
        if (this.$scope.metric.id !== args.task.parentId) {
          this.$scope.taskList.splice(i, 1);
          return;
        }

        // and update task in list
        this.$scope.taskList[i] = args.task;
        return;
      }
    }
  };

  private getTasks = () => {
    this.$scope.indicators.gettingTasks = true;
    this.tasksService.getTasksV2({ filter: { parentId: this.$stateParams.metricId }, sort: "-dueDate" }).then(
      (result) => {
        this.$scope.indicators.gettingTasks = false;
        this.$scope.taskList = result.items;
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "gettingTasks", inline: true })
    );
  };

  private confirmDeleteSnapshot = (snapshotId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    const modal = this.$uibModal.open({
      templateUrl: require("../views/confirm-delete-metric-snapshot.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope) {
          $scope.confirm = () => {
            modal.close();
            ctrl.deleteSnapshot(snapshotId);
          };
        },
      ],
    });
  };

  private deleteSnapshot = (snapshotId: string): void => {
    this.metricService.deleteSnapshot(this.$scope.metric.goalId, this.$scope.metric.id, snapshotId).then(
      () => {
        this.okrEvents.broadcastGoalsChanged("goalChanged");
        this.$rootScope.$broadcast("goalsChanged");
      },
      (err) => this.uiErrorHandlingService.handleModal(err)
    );
  };

  private getMetric = (metricId: string): IPromise<IMetric> => {
    // to do: add field projection with https://gtmhub.atlassian.net/browse/GVS-10052
    if (metricId) {
      const filter = { _id: metricId };

      return this.metricService.getMetricsV2({ filter }).then((collection) => {
        if (collection.items.length) {
          return collection.items[0];
        } else {
          return this.$q.reject(customNotFoundError());
        }
      });
    }
  };

  private refreshMetric = () => {
    this.$scope.indicators.refreshingMetric = true;
    this.$scope.indicators.dontDisplaySnapshots = true;

    this.getMetric(this.$stateParams.metricId).then(
      (metric) => {
        this.$scope.indicators.refreshingMetric = false;

        this.processMetric(metric);
        this.processContributingKRs(metric);
      },
      (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "refreshingMetric" })
    );
  };

  private refresh = () => {
    this.$scope.indicators.loadingUpdate = true;
    this.load();
  };

  private getGoal = (goalId: string): IPromise<unknown> => {
    const goalFields = "dateFrom,dateTo,id,name,ownerId,sessionId,locked,private,closedStatus";

    return this.goalService.getGoalWithFields(goalId, goalFields).then((goal) => {
      this.$scope.goal = goal;
      this.setMetricMethodology();

      if (goal.sessionId) {
        return this.getSession(goal.sessionId);
      }
    });
  };

  private setMetricMethodology(): void {
    this.$scope.methodologySettings = this.okrMethodologyFacade.getMethodologySettingsUIForMetricView(this.$scope.planningSession);
  }

  private getSession = (id: string): void => {
    this.$scope.planningSession = this.$scope.sessionIdMap[id];
    this.measureDueDateDifference(this.$scope.metric);
    this.initAllowedActions();
  };

  // function which counts the same appearances in an object
  private countIds = (arr: IComment[]): INumericMap => {
    return arr.reduce((snapshotIdCommentsCountMap: INumericMap, currentComment: IComment): INumericMap => {
      const targetId = currentComment.targetId;

      // we increment the counter if it appear
      snapshotIdCommentsCountMap[targetId] = Object.prototype.hasOwnProperty.call(snapshotIdCommentsCountMap, targetId) ? snapshotIdCommentsCountMap[targetId] + 1 : 1;

      // at the end we have object with the counted appearanes exampale {something: 3, else: 2}
      return snapshotIdCommentsCountMap;
    }, {} as INumericMap);
  };

  private generateCalls = (metrics: string[], targetType: string, index = 0): { [index: string]: IPromise<ICollection<any>> } => {
    const promises: { [index: string]: IPromise<ICollection<any>> } = {};
    const constraints = 67;
    // url characters constraints are 2083, so we check how many targetIds can be passed at once
    // the endConstraint is 67 because the part of the url that is not ids is 80 characters and each targetId is 25
    let startConstraints = 0;
    let endConstraints: number = constraints;

    // how many times we have to make a call, because of the url length constraints
    const length: number = Math.ceil(metrics.length / endConstraints);

    for (let i = 0; i < length; i++) {
      // segmentIds are only the allowed ids for the url length condition
      const segmentIds = metrics.slice(startConstraints, endConstraints);

      if (targetType === this.strings.comments) {
        promises[i + index] = this.commentService.getComments({ targetType: "checkin", targetIds: segmentIds.join(",") });
      } else if (targetType === this.strings.reactions) {
        promises[i + index] = this.reactionService.getReactions({ targetIds: segmentIds.join(",") });
      }

      // update the constraints conditions
      startConstraints = endConstraints;
      endConstraints += constraints;
    }

    return promises;
  };

  private processContributingKRs = (metric: IMetric): void => {
    if (!metric.cascadeType) {
      return;
    }

    const totalAmount: IMetric = angular.copy(metric);
    const difference: IMetric = angular.copy(metric);
    totalAmount.target = metric.initialValue || 0;

    if (metric.links && metric.links.spec.cascading && metric.links.expanded) {
      const expandedMetrics = metric.links.expanded.metrics.reduce((result, expandedMetric) => {
        result[expandedMetric.id] = expandedMetric;
        return result;
      }, {});

      totalAmount.target = metric.links.spec.cascading.reduce((result, linkSpec) => {
        const linkedMetric = expandedMetrics[linkSpec.id];
        if (linkedMetric) {
          result += linkedMetric.target;
        }
        return result;
      }, totalAmount.target);
    }

    difference.target = Math.abs(metric.target - totalAmount.target);
    const isCovered = metric.target <= totalAmount.target;

    this.$scope.contributingKRsTarget = {
      isCovered,
      totalAmount,
      difference,
    };
  };

  private processMetric = (metric: IMetric): IPromise<unknown> => {
    const metricsIds: string[] = [];

    const metricSnapshotMap: { [id: string]: IMetricSnapshot } = {};
    this.$scope.reactions = {};

    if (metric.metricHistory && metric.metricHistory.length > 0) {
      for (let i = 0; i < metric.metricHistory.length; i++) {
        if (dayjs(metric.metricHistory[i].updatedAt).format("YYYY-MM-DD") !== dayjs(metric.metricHistory[i].createdAt).format("YYYY-MM-DD")) {
          metric.metricHistory[i].updatedLate = true;
        }

        // array with all comments ids
        // some older comments don't have targetId, that's why we make this check
        if (metric.metricHistory[i].id !== undefined && metric.metricHistory[i].id !== "") {
          metricsIds.push(metric.metricHistory[i].id);
          metricSnapshotMap[metric.metricHistory[i].id] = metric.metricHistory[i];
          this.$scope.reactions[metric.metricHistory[i].id] = [];
        }

        metric.metricHistory[i].view = {
          color: null,
        };

        metric.metricHistory[i].view.color = this.progressColoring.getColorOrGeneric(metric.metricHistory[i].currentAttainment);
      }

      const commentsPromise: { [index: string]: IPromise<ICollection<IComment>> } = this.generateCalls(metricsIds, this.strings.comments);
      const commentsPromisesLength = Object.keys(commentsPromise).length;

      const reactionsPromise: { [index: string]: IPromise<ICollection<IAggregatedReaction>> } = this.generateCalls(
        metricsIds,
        this.strings.reactions,
        commentsPromisesLength
      );

      const promiseMap: { [index: string]: IPromise<any> } = {
        ...commentsPromise,
        ...reactionsPromise,
      };

      // resolve the promises
      return this.$q.all(promiseMap).then(
        (resolvedMap: { [id: string]: any }) => {
          let countByTargetId: INumericMap;

          for (let i = 0; i < commentsPromisesLength; i++) {
            if (Object.prototype.hasOwnProperty.call(resolvedMap, i)) {
              // group comments by targetId(their parent comment id)
              countByTargetId = this.countIds(resolvedMap[i].items);

              // setting the count of the comments which is used in the view
              for (const key in metricSnapshotMap) {
                if (Object.prototype.hasOwnProperty.call(metricSnapshotMap, key)) {
                  if (countByTargetId[key]) {
                    metricSnapshotMap[key].commentsCount = countByTargetId[key];
                  } else if (!countByTargetId[key] && !metricSnapshotMap[key].commentsCount) {
                    metricSnapshotMap[key].commentsCount = 0;
                  }
                }
              }
            }
          }

          const resolvedMapLength = Object.keys(resolvedMap).length;

          for (let i = commentsPromisesLength; i < resolvedMapLength; i++) {
            resolvedMap[i].items.forEach((reaction: IAggregatedReaction) => {
              if (!this.$scope.reactions[reaction.targetId]) {
                this.$scope.reactions[reaction.targetId] = [];
              }

              this.$scope.reactions[reaction.targetId].push(reaction);
            });
          }

          this.$scope.metric = metric;

          this.$timeout(() => {
            this.$scope.indicators.dontDisplaySnapshots = false;
          }, 50);
        },
        (err) => {
          this.uiErrorHandlingService.handleModal(err);
        }
      );
    } else {
      this.$scope.indicators.dontDisplaySnapshots = false;
      this.$scope.metric = metric;
    }
  };

  private getSnapshotSortField = (snapshot: IMetricSnapshot): number => {
    return dayjs(snapshot.updatedAt).valueOf();
  };

  private getSnapshotOrigin(snapshot: IMetricSnapshot, metric: IMetric, sourceMetric: IMetric): SnapshotOrigin {
    const isUserAction = !!snapshot.changedBy;
    const isCascadingUpdate = !!snapshot.sourceInfo;
    const isCascadingMetric = metric.cascadeType !== "";
    const isDynamicMetric = (metric.sourceMetricId && sourceMetric.insightName !== "") || (!metric.sourceMetricId && metric.insightName !== "");
    const hasComment = !!snapshot.comment;

    if (isCascadingUpdate) {
      return "cascading_update";
    } else if (!isUserAction) {
      return "automatic_update";
    } else if ((isCascadingMetric || isDynamicMetric) && hasComment && snapshot.valueChange === 0) {
      return "notes_update";
    }

    return "manual_update";
  }

  private getFormattedValue = (snapshot: IMetricSnapshot): string => {
    return this.$filter<MetricValueFilter>("metricValue")(snapshot.metricValue, this.$scope.metric.format);
  };

  private redirectToGoal = (sessionId: string, goalId: string) => {
    if (this.$rootScope.isMsTeamsState) {
      return;
    }

    const params = {
      planningSessionId: sessionId,
      id: goalId,
    };

    if (this.isIncludesInStateName(".all.")) {
      this.$state.go("gtmhub.goals.all._goalSidebar.goal", params);
      return;
    }
    if (this.isIncludesInStateName(".alignment.")) {
      this.$state.go("gtmhub.goals.alignment._goalSidebar.goal", params);
      return;
    }
    if (this.isIncludesInStateName("alignmentSidebar.tree")) {
      this.$state.go("gtmhub.goals.alignmentSidebar.tree._goalSidebar.goal", params);
      return;
    }
    if (this.isIncludesInStateName(".home.")) {
      this.$state.go("gtmhub.home._goalSidebar.goal", params);
      return;
    }

    if (this.isIncludesInStateName(".msTeamsHome.")) {
      this.$state.go("gtmhub.msTeamsHome._goalSidebar.goal", params);
      return;
    }

    if (this.isIncludesInStateName(".insightboard.")) {
      this.$state.go("gtmhub.insightboard._goalSidebar.goal", { ...params, returnState: "gtmhub.insightboard" });
      return;
    }

    this.$state.go("gtmhub.goals.all._goalSidebar.goal", params);
  };

  private isIncludesInStateName = (stringToCheck: string): boolean => {
    return this.$state.current.name.includes(stringToCheck);
  };

  private onReactionCreated = (event: IAngularEvent, newReaction: IReaction): void => {
    if (this.$scope.reactions[newReaction.targetId]) {
      this.$scope.reactions[newReaction.targetId] = this.reactionService.onReactionCreated(this.$scope.reactions[newReaction.targetId], newReaction);
    }
  };

  private onReactionDeleted = (event: IAngularEvent, deletedReaction: IAggregatedReaction): void => {
    if (this.$scope.reactions[deletedReaction.targetId]) {
      this.$scope.reactions[deletedReaction.targetId] = this.reactionService.onReactionDeleted(this.$scope.reactions[deletedReaction.targetId], deletedReaction);
    }
  };

  private getMetricActions = (metricId: string, type = "metric"): void => {
    this.permissionsService.getCurrentUserAllowedActionsPerItem(metricId, type).then(
      (actions) => {
        this.metricActions = actions.currentUserActions;
      },
      (err) => this.uiErrorHandlingService.handleModal(err)
    );
  };

  private openDisableSyncModal = (metricId: string): void => {
    this.disableMetricSync.open(metricId);
  };

  private getSourceMetric = (metricId: string): void => {
    if (metricId) {
      const filter = { _id: metricId };
      const fields = "id,insightName";

      this.metricService
        .getMetricsV2({ filter, fields })
        .then((collection) => {
          // it should always be one element
          this.$scope.sourceMetric = collection.items[0] || {};
        })
        .catch((err) => this.uiErrorHandlingService.handleModal(err));
    }
  };

  private copyItemUrlToClipboard = (): void => {
    const copyToClipboardOptions = { container: getCopyURLNotificationEl(), duration: 3000 };
    const url = this.getShareableUrl();

    copyToClipboard(url, copyToClipboardOptions);
  };

  private getShareableUrl = (): string => {
    const states = {
      alignmentTree: "gtmhub.goals.alignmentSidebar.tree",
      grid: "gtmhub.goals.all._metric.metric",
    };

    if (this.isIncludesInStateName(states.alignmentTree)) {
      return this.$state.href(
        states.alignmentTree,
        { ...this.$stateParams, sharedId: this.$scope.metric.id, sharedParentId: this.$scope.metric.goalId },
        { absolute: true }
      );
    }

    const isHomeOrList = this.isIncludesInStateName(".home.") || this.isIncludesInStateName(".msTeamsHome.") || this.isIncludesInStateName("gtmhub.list");
    if (isHomeOrList) {
      return this.$state.href(states.grid, { metricId: this.$scope.metric.id, planningSessionId: this.$scope.metric.sessionId, sharedMetric: true }, { absolute: true });
    }

    return this.$state.href(this.$state.current, { ...this.$stateParams, sharedMetric: true }, { absolute: true });
  };

  private getContributingMetricsNames = (metric: IMetric): IPromise<unknown> => {
    const cascadingMetricsIds = metric.links?.spec?.cascading?.map((cascading) => cascading.id) || [];
    if (cascadingMetricsIds.length) {
      return this.metricService.getMetricsV2({ filter: { _id: { $in: cascadingMetricsIds } }, fields: "name,goalId" }).then((metrics) => {
        this.$scope.contributingMetrics = metrics.items;
      });
    }
  };
}
